// Shared vocabulary and formatting for the listing sources screen.
//
// The five outcome words are the screen's whole language, so they are defined
// once here rather than spelled out per component. "skipped" reads as "Left
// alone" throughout: it is the crawler deliberately not touching something,
// which is a success, and calling it "skipped" makes a correct decision look
// like a miss.

// Filter order on the run detail screen, as the design lays it out.
export const OUTCOMES = ['updated', 'created', 'skipped', 'failed'];

export const OUTCOME_LABEL = {
    created: 'Created',
    updated: 'Updated',
    unchanged: 'Unchanged',
    skipped: 'Left alone',
    failed: 'Failed',
};

// The order the counts are always read in, on every card.
export const COUNT_KEYS = ['created', 'updated', 'unchanged', 'skipped', 'failed'];

export const STATUS_LABEL = {
    running: 'Running',
    completed: 'Completed',
    failed: 'Failed',
};

export function formatNumber(value) {
    const number = Number(value || 0);
    return number.toLocaleString('en-US');
}

/** The design's pager label: "1–20 of 118". */
export function formatRange(meta, shown) {
    if (!meta) return '';
    if (!meta.total) return '0 of 0';
    const from = (meta.current_page - 1) * meta.per_page + 1;
    const to = from + shown - 1;
    return formatNumber(from) + '–' + formatNumber(to) + ' of ' + formatNumber(meta.total);
}

// Columns whose values are quantities, and read better in thousands.
const GROUPED = ['LIST_PRICE', 'SQFT', 'NET_OPERATING_INCOME', 'PROFORMA_NOI', 'UNITS'];

// Quantities too, but small ones: 1.14 acres, a 7.4% cap rate, a latitude.
const DECIMAL = ['ACRES', 'CAPITALIZATION_RATE', 'PROFORMA_CAP_RATE', 'VACANCY', 'LATITUDE', 'LONGITUDE'];

/**
 * A value as it should be read, which depends on the column it came from.
 *
 * Everything not named above is shown exactly as stored. An APN and a ZIP are
 * digits but they are not numbers: grouping them turns parcel 1162910000001
 * into "1,162,910,000,001" and ZIP 75038 into "75,038", which is how an audit
 * trail stops being usable for looking anything up.
 *
 * A stored "from" arrives as a string ("1569625.00") and the "to" as a number,
 * so both go through the same rounding — otherwise an unchanged figure reads
 * as 1569625.00 → 1569625.
 */
export function formatValue(value, column) {
    if (value === null || value === undefined || value === '') return '—';

    const grouped = GROUPED.indexOf(column) !== -1;
    const decimal = DECIMAL.indexOf(column) !== -1;

    if (!grouped && !decimal) return String(value);

    const number = Number(value);
    if (isNaN(number)) return String(value);

    const rounded = Math.round(number * 100) / 100;
    return grouped ? rounded.toLocaleString('en-US') : String(rounded);
}

function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * "Last night, 03:00" beats "2026-08-02T03:00:00Z" for the question actually
 * being asked, which is almost always about last night.
 */
export function formatRunTime(iso) {
    if (!iso) return '—';

    const date = new Date(iso);
    const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

    if (days === 0) return 'Today, ' + time;
    if (days === 1) return 'Last night, ' + time;

    const day = date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
    return day + ', ' + time;
}

export function formatClock(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * The zone the schedule is written in, shortened for the places a full IANA
 * name would crowd the line out. Unknown zones keep their full name rather
 * than being abbreviated into something wrong.
 */
const ZONE_ABBREVIATIONS = {
    'America/New_York': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Phoenix': 'MST',
    'America/Los_Angeles': 'PT',
    'America/Anchorage': 'AKT',
    'Pacific/Honolulu': 'HST',
};

export const TIMEZONES = [
    ['America/New_York', 'Eastern'],
    ['America/Chicago', 'Central'],
    ['America/Denver', 'Mountain'],
    ['America/Phoenix', 'Arizona'],
    ['America/Los_Angeles', 'Pacific'],
    ['America/Anchorage', 'Alaska'],
    ['Pacific/Honolulu', 'Hawaii'],
];

export function zoneAbbreviation(zone) {
    return ZONE_ABBREVIATIONS[zone] || zone;
}

export const STATES = [
    ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
    ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
    ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
    ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
    ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
    ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
    ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
    ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
    ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
    ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
    ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

export const STATE_NAMES = STATES.reduce((names, state) => {
    names[state[0]] = state[1];
    return names;
}, {});

/**
 * A change row's shape: whether the crawler filled a column, changed one, or
 * cleared one. A clearing is not a change and must not read as one — three of
 * the four rows in a reclassification are removals.
 */
export function changeKind(from, to) {
    const blank = (value) => value === null || value === undefined || value === '';
    if (blank(to)) return 'cleared';
    if (blank(from)) return 'added';
    return 'changed';
}
